'use client';

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from '@/providers/StoreProvider';
import { ChatDetail } from '@/components/chat/ChatDetail';
import { Button } from '@/components/ui';
import { useIsMobile, useDeviceType } from '@/hooks/useDeviceType';
import { ChatList } from '@/components/chat/ChatList';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAuthModal } from '@/hooks/useAuthModal';
import type { Chat } from '@repo/api';
import { MessagesSkeleton } from './MessagesSkeleton';

interface MessagesClientProps {
  chatId: string | null;
  unitSlug: string | null;
}

export default function MessagesClient({ chatId, unitSlug }: MessagesClientProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { width: screenWidth } = useDeviceType();
  const analytics = useAnalytics();

  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const chatSlice = useStore(s => s.chat);
  const { chatList, isFetchingChatList, fetchChatList, chatDetailData } = chatSlice;
  const { openAuthFlow } = useAuthModal();

  const [selectedChatId, setSelectedChatId] = useState<string | null>(chatId);
  const [selectedUnitSlug, setSelectedUnitSlug] = useState<string | null>(unitSlug || null);
  const didAutoSelectRef = useRef(false);

  const header = useMemo(() => <h1 className="text-heading-6 text-primary">Messages</h1>, []);

  const handleLogin = () => {
    void openAuthFlow({
      title: 'Log in or Sign up',
      description: 'Log in to view and reply to your messages or sign up to get started.',
      locked: false,
    });
  };

  const handleBrowseListings = () => {
    router.push('/listings/status:verified');
  };

  // Fetch chat list on mount and when logged in
  useEffect(() => {
    if (isLoggedIn && chatList.length === 0 && !isFetchingChatList) {
      void fetchChatList(1);
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track viewing the messages inbox (once per mount)
  useEffect(() => {
    analytics.track('view_messages');
  }, [analytics]);

  // Sort chats by latest message time
  const sortedChats = useMemo(() => {
    return [...chatList].sort((a, b) => {
      const aTime = a.latestMessage?.atCreated || '0';
      const bTime = b.latestMessage?.atCreated || '0';
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }, [chatList]);

  const handleChatSelect = useCallback(
    (chat: Chat, opts?: { replaceUrl?: boolean }) => {
      // Don't change selection if chat is loading
      const currentChatId = chatDetailData?.chat?.id;
      const isCurrentChatLoading = currentChatId === chat.id && chatSlice.chatDetail.length === 0;
      if (isCurrentChatLoading) {
        return;
      }
      const hadUnreadMessages = !!chat.hasUnreadMessages;
      // Optimistically update local store (don't mutate `chat` directly)
      chatSlice.markChatAsReadLocal(chat.id);

      analytics.track('chat_list_item_select', { hasUnreadMessages: hadUnreadMessages });

      const unitData = chat.unit as any;
      const fullAddress = unitData?.fullAddress || unitData?.address || '';
      const streetAddress = String(fullAddress).split(',')[0]?.trim() || '';
      analytics.track('opened_message', { address: streetAddress });

      setSelectedChatId(chat.id);
      setSelectedUnitSlug(chat.unit.slug);
      const url = `/messages?chat_id=${chat.id}&unit_slug=${chat.unit.slug}`;
      if (opts?.replaceUrl) {
        router.replace(url, { scroll: false });
      } else {
        router.push(url, { scroll: false });
      }
    },
    [analytics, router, chatDetailData?.chat?.id, chatSlice.chatDetail.length],
  );

  // Update selected chat from URL - clear state when URL params are removed
  useEffect(() => {
    if (chatId && unitSlug) {
      setSelectedChatId(chatId);
      setSelectedUnitSlug(unitSlug);
    } else {
      // Clear state when URL params are removed (e.g., when clicking back)
      setSelectedChatId(null);
      setSelectedUnitSlug(null);
    }
  }, [chatId, unitSlug]);

  // Auto-select first chat if none selected and chats exist (large desktop screens only - >= 1280px)
  useEffect(() => {
    // Important: use replace (not push) for automatic URL updates so the browser back button
    // returns to the previous page instead of stepping through /messages -> /messages?chat_id...
    if (
      screenWidth >= 1280 &&
      isLoggedIn &&
      !selectedChatId &&
      !chatId &&
      !unitSlug &&
      sortedChats.length > 0
    ) {
      if (didAutoSelectRef.current) return;
      didAutoSelectRef.current = true;
      const firstChat = sortedChats[0];
      if (firstChat) {
        handleChatSelect(firstChat, { replaceUrl: true });
      }
    }
  }, [selectedChatId, sortedChats, isLoggedIn, handleChatSelect, screenWidth, chatId, unitSlug]);

  // Logged-out state (mobile parity with Saved page)
  if (!isLoggedIn) {
    return (
      <div className="grid h-[calc(100vh-220px)] grid-rows-[auto_1fr] grid-cols-4 md:grid-cols-8 lg:grid-cols-12 px-4 gap-8 max-w-screen-xl mx-auto pt-8">
        <div className="lg:col-start-4 col-span-full">
          <div className="dynamic-grid-container">{header}</div>
        </div>
        <div className="px-4 lg:col-start-4 col-span-full text-center md:text-left md:col-span-5 flex flex-col md:items-start md:justify-start items-center justify-center md:px-12 gap-6 w-full">
          <div className="flex flex-col gap-2 w-full">
            <p className="text-heading-6 text-primary">Log in to see your messages</p>
            <p className="text-body-2 text-secondary">
              Log in to view all your messages or sign up to get started with your apartment search.
            </p>
          </div>
          <div className="w-full md:w-fit">
            <Button fullWidth onClick={handleLogin}>
              Log in or sign up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isFetchingChatList) {
    return <MessagesSkeleton />;
  }

  // Empty state (no chats yet) – reuse Saved page structure
  if (chatList.length === 0) {
    return (
      <div className="grid h-[calc(100vh-220px)] grid-rows-[auto_1fr] grid-cols-4 md:grid-cols-8 lg:grid-cols-12 px-4 gap-8 max-w-screen-xl mx-auto pt-8">
        <div className="lg:col-start-4 col-span-full">
          <div className="dynamic-grid-container">{header}</div>
        </div>
        <div className="px-4 lg:col-start-4 col-span-full text-center md:text-left md:col-span-5 flex flex-col md:items-start md:justify-start items-center justify-center md:px-12 gap-6 w-full">
          <div className="flex flex-col gap-2 w-full">
            <p className="text-heading-6 text-primary">You don't have any messages yet</p>
            <p className="text-body-2 text-secondary">
              Start exploring apartments on Openigloo to find your dream apartment.
            </p>
          </div>
          <div className="w-full md:max-w-[420px]">
            <Button fullWidth onClick={handleBrowseListings}>
              View verified listings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // On mobile: show full-screen MessageWindow when chat is selected, otherwise show ChatList
  // Use URL params directly for conditional rendering to ensure it updates immediately
  if (isMobile) {
    if (chatId && unitSlug) {
      const selectedChat = sortedChats.find(chat => chat.id === chatId);
      return (
        <div key={`chat-${chatId}`} className="fixed inset-0 z-50 bg-primary">
          <ChatDetail
            variant="page"
            key={chatId}
            chatId={chatId}
            unitSlug={unitSlug}
            {...(selectedChat?.unit && { initialUnitData: selectedChat.unit as any })}
          />
        </div>
      );
    }
    // Mobile: Show chat list when no chat selected - enable bottom nav
    return (
      <div className=" overflow-hidden w-full">
        <h2 className="text-body-1-high text-primary p-6">Messages</h2>
        <div className="px-4 w-full h-[calc(100vh-212px)] pb-16 overflow-y-auto overflow-x-hidden bg-primary">
          <ChatList chats={sortedChats} selectedChatId={chatId} onChatSelect={handleChatSelect} />
        </div>
      </div>
    );
  }

  // Desktop: Show both columns side-by-side using 12-column grid
  return (
    <div className="h-[calc(100vh-92px)] overflow-hidden pb-8">
      <div className="h-full grid grid-cols-4 md:grid-cols-8 gap-x-8 lg:grid-cols-12 max-w-screen-xl mx-auto grid-rows-[auto_1fr]">
        <div className="col-start-2 col-span-full py-8 px-4">
          <h4 className="text-heading-4 text-primary">Messages</h4>
        </div>
        {/* Left Column: Chat List - starts from column 2, occupies 4 columns */}
        <div className="col-start-2 col-span-4 pl-4 overflow-y-auto overflow-x-hidden bg-primary">
          <ChatList
            chats={sortedChats}
            selectedChatId={selectedChatId}
            onChatSelect={handleChatSelect}
          />
        </div>
        {/* Right Column: Message Window - starts from column 6, occupies 6 columns with left padding */}
        <div className="hidden lg:block col-start-6 col-span-6 bg-primary overflow-hidden">
          {selectedChatId && selectedUnitSlug ? (
            (() => {
              const selectedChat = sortedChats.find(chat => chat.id === selectedChatId);
              return (
                <ChatDetail
                  variant="page"
                  key={selectedChatId}
                  chatId={selectedChatId}
                  unitSlug={selectedUnitSlug}
                  {...(selectedChat?.unit && { initialUnitData: selectedChat.unit as any })}
                />
              );
            })()
          ) : (
            <div className="flex h-full items-center justify-center text-body-2 text-secondary">
              <div className="text-center">
                <p>Select a chat to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
